import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { canShowAppleIAP } from '../../shared/utils/platform';
import { getCachedAuthUser } from '../../services/network';
import { isAnySheetOpen, useAnySheetOpen } from '../../shared/components/BottomSheet';
import { useSubscription } from './useSubscription';
import { useEntitlement } from './useEntitlement';

/**
 * useLapsedRedirect — mounted once in Layout (docs/v150-spec.md §3.5).
 *
 * When Premium has just ended — an Apple trial or subscription, a referral
 * pass or the old preview, as the server's `lapsed` says — send the user to
 * the plans ONCE: /paywall?src=lapsed&reason=<kind>. Only on iOS (the only
 * place Premium can be bought), only on Free with no Premium shared by a
 * baby's owner, only within 30 days of the end, and never while the user is
 * already on the paywall, in onboarding or on the offer page.
 *
 * It waits for useEntitlement's `settled` (v1.5.1, docs/v151-spec.md §6.2):
 * the own status, the babies list and the baby-scoped plan must all have
 * answered. A list still loading, a baby not yet selected, a failed or
 * offline-paused fetch would otherwise read as "not shared" and send a
 * caregiver covered by the owner's Premium to the paywall.
 *
 * The acknowledgement is stored per Cognito sub and per endedAt — another
 * account on the device, or a later lapse, gets its own redirect. It is written
 * when the redirect fires, so closing the app on the paywall does not bring
 * the user straight back to it.
 *
 * v1.5.3 review (docs/v153-spec.md §0.4, "the ask never interrupts"): never
 * while a bottom sheet is open. The dashboard's log, sleep and Atlas sheets
 * portal over '/', and /paywall sits outside Layout, so the redirect would
 * unmount a half-filled form. Nothing is acknowledged while a sheet is open;
 * the redirect is judged again when the last one closes.
 */
export const LAPSED_ACK_PREFIX = 'bt_lapsed_ack:';
const WINDOW_MS = 30 * 86400000;
const KINDS = ['trial', 'subscription', 'pass', 'preview'];
const QUIET_PATHS = ['/paywall', '/onboarding', '/offer'];

function readAck(sub) {
  try {
    return localStorage.getItem(`${LAPSED_ACK_PREFIX}${sub}`);
  } catch {
    return null;
  }
}

function writeAck(sub, endedAt) {
  try {
    localStorage.setItem(`${LAPSED_ACK_PREFIX}${sub}`, endedAt);
    return true;
  } catch {
    return false;
  }
}

export function useLapsedRedirect() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { plan, lapsed, isLoading } = useSubscription();
  const { sharedByOwner, isLoading: entitlementLoading, settled } = useEntitlement();
  // Any BottomSheet open in the app (the dashboard's log, sleep and Atlas sheets).
  const sheetOpen = useAnySheetOpen();

  const kind = lapsed?.kind;
  const endedAt = lapsed?.endedAt || null;

  useEffect(() => {
    if (!canShowAppleIAP() || isLoading || entitlementLoading || !settled) return;
    if (plan !== 'free' || sharedByOwner || !endedAt || !KINDS.includes(kind)) return;
    if (QUIET_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`))) return;
    // Never over an open sheet (v1.5.3 review, docs/v153-spec.md §0.4): the
    // sheets portal over '/', and /paywall sits outside Layout, so navigating
    // would unmount a half-filled form. Checked before the acknowledgement;
    // `sheetOpen` is a dependency, so the redirect is judged again when the
    // last sheet closes. The live count covers a sheet that opened in this
    // same commit (its effect has run, `sheetOpen` not yet).
    if (sheetOpen || isAnySheetOpen()) return;
    const endedMs = Date.parse(endedAt);
    if (!Number.isFinite(endedMs) || Date.now() - endedMs > WINDOW_MS) return;
    const sub = getCachedAuthUser()?.sub;
    if (!sub || readAck(sub) === endedAt) return;
    // No storage → no way to remember it: better never than on every launch.
    if (!writeAck(sub, endedAt)) return;
    navigate(`/paywall?src=lapsed&reason=${kind}`);
  }, [plan, kind, endedAt, sharedByOwner, isLoading, entitlementLoading, settled, pathname, sheetOpen, navigate]);
}

export default useLapsedRedirect;
